
import {
  computed, defineComponent, ref, watch,
} from 'vue';
import moment from 'moment-timezone';
import { useStore } from '@/store';
import { useI18n } from 'vue-i18n';
import PageHeader from '@/components/layout/PageHeader.vue';
import { useRouter } from 'vue-router';
import { CampaignsBulkStatusEnum } from '@/types';
import { NestApiType, nestCall } from '@/composables/nestApi';
import { showToastSuccess, showToastError } from '@/helpers';
import { UserState } from '@/composables/User';
import {
  getChannelIcon,
  getChannelTagStyleVars,
  getStatusIcon,
  getStatusStyleVars,
} from '@/views/automated-scenarios/utils/styleTokens';
import SpmTableAPI from '@/components/table-rest-api/SpmTableAPI.vue';
import SpmBadge from '@/components/table-rest-api/SpmBadge.vue';
import SpmDateDisplay from '@/components/table-rest-api/SpmDateDisplay.vue';
import SpmMetricDisplay from '@/components/table-rest-api/SpmMetricDisplay.vue';
import SpmHelpIcon from '@/components/table-rest-api/SpmHelpIcon.vue';
import {
  formatPercentage as formatPercentageUtil,
  formatCurrency,
  formatInteger as formatIntegerUtil,
} from '@/components/table-rest-api/formatters';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import SpmEmptyState from '@/components/table-rest-api/SpmEmptyState.vue';
import Dialog from 'primevue/dialog';
import ConfirmDialog from 'primevue/confirmdialog';
import { useConfirm } from 'primevue/useconfirm';
import ImportModal from '@/components/import/ImportModal.vue';
import CampaignsBulkBaseSelectorV2 from '@/components/automated-scenarios/CampaignsBulkBaseSelectorV2.vue';
import IntegrationOwnerBadge from '@/components/integrations/IntegrationOwnerBadge.vue';
import type {
  SpmColumnConfig,
  ApiCallConfig,
  SpmApiFilter,
  SpmActiveSort,
  SelectionInfo,
  ExportPayload,
  SpmTableAPIExposed,
} from '@/components/table-rest-api/types';
import { SpmFilterOperator } from '@/components/table-rest-api/filters';
import { isDemoModeActive, demoCampaigns } from '@/composables/DemoMode';
import {
  DuplicateBulkCampaignAction,
  DeleteBulkCampaignAction,
  ArchiveBulkCampaignAction,
  SuspendBulkCampaignAction,
  PublishToGalleryDialog,
  useBulkCampaignsActions,
} from './BulkCampaignsActions';
import type { BulkCampaignData } from './BulkCampaignsActions/types';

export default defineComponent({
  name: 'CampaignsBulkList',

  components: {
    SpmTableAPI,
    PageHeader,
    SpmBadge,
    SpmDateDisplay,
    SpmMetricDisplay,
    SpmHelpIcon,
    SpmButton,
    SpmEmptyState,
    Dialog,
    ConfirmDialog,
    ImportModal,
    CampaignsBulkBaseSelectorV2,
    IntegrationOwnerBadge,
    DuplicateBulkCampaignAction,
    DeleteBulkCampaignAction,
    ArchiveBulkCampaignAction,
    SuspendBulkCampaignAction,
    PublishToGalleryDialog,
  },

  setup() {
    const { t } = useI18n();
    const router = useRouter();
    const store = useStore();
    const confirm = useConfirm();
    const tableRef = ref<SpmTableAPIExposed | null>(null);

    // Watch flow editor visibility to reload table when editor closes
    const isFlowEditorVisible = computed(() => store.getters['flowEditor/isEditorVisible']);
    watch(isFlowEditorVisible, (newValue, oldValue) => {
      // Reload table when editor closes (visible -> hidden)
      if (oldValue === true && newValue === false && tableRef.value?.reloadData) {
        tableRef.value.reloadData();
      }
    });

    // Action component refs
    const duplicateRef = ref(null);
    const deleteRef = ref(null);
    const archiveRef = ref(null);
    const suspendRef = ref(null);
    const publishGalleryRef = ref<{ open: (list: { id: number; name: string }[]) => void; done: () => void } | null>(null);

    // Modal states
    const showNeedOfferModal = ref(false);
    const showImportModal = ref(false);
    const showCreateModal = ref(false);

    // Watch import modal visibility to reload table when editor closes
    watch(showImportModal, (newValue, oldValue) => {
      // Reload table when editor closes (visible -> hidden)
      if (oldValue === true && newValue === false && tableRef.value?.reloadData) {
        tableRef.value.reloadData();
      }
    });

    // Client timezone for date display
    const clientTimezone = moment.tz.guess() || 'UTC';

    // Hidden statuses (not shown in the list)
    const hiddenStatuses: CampaignsBulkStatusEnum[] = [
      CampaignsBulkStatusEnum.ARCHIVED,
      CampaignsBulkStatusEnum.ARCHIVED_BY_USER,
      CampaignsBulkStatusEnum.DELETED,
    ];

    // Status options for filter
    const statusOptions = computed(() => (Object.values(CampaignsBulkStatusEnum) as CampaignsBulkStatusEnum[])
      .filter((status) => !hiddenStatuses.includes(status))
      .map((status) => ({
        label: t(`campaignsBulkList.status.${status}`),
        value: status,
      })));

    // API call configuration.
    // `withStats=false` : le listing revient sans aucune métrique (rapide) ;
    // les colonnes `lazy` sont remplies ensuite via `loadCampaignsStats`.
    const apiCall = ref<ApiCallConfig>({
      type_api: NestApiType.CLIENT_API,
      path: '/campaigns/bulk?withStats=false',
      secure: true,
    });

    /**
     * Stats différées des colonnes lazy : un appel scopé sur les ids du lot,
     * réponse indexée par clé de ligne pour le merge dans le tableau.
     */
    const loadCampaignsStats = async (
      rows: Record<string, unknown>[],
    ): Promise<Record<string, Record<string, unknown>> | null> => {
      const ids = rows.map((row) => row.id).filter(Boolean);
      if (!ids.length) {
        return null;
      }

      const response = await nestCall(
        'get',
        NestApiType.CLIENT_API,
        '/campaigns/bulk/stats',
        {},
        '',
        undefined,
        true,
        { ids: ids.join(',') },
      );

      if (!response?.data || !Array.isArray(response.data)) {
        return null;
      }

      return Object.fromEntries(
        response.data.map(
          (stats: Record<string, unknown>): [string, Record<string, unknown>] => [String(stats.id), stats],
        ),
      );
    };

    // Persistent filters - exclude hidden statuses + demo mode filter
    const persistentFilters = computed<SpmApiFilter[]>(() => {
      const filters = hiddenStatuses.map((status) => ({
        field: 'status',
        operator: SpmFilterOperator.NOT_EQUALS,
        values: status,
      }));

      // In demo mode, only show selected campaigns (or none if empty)
      if (isDemoModeActive.value) {
        const ids = demoCampaigns.bulkIds;
        filters.push({
          field: 'id',
          operator: SpmFilterOperator.IN,
          values: (ids.length > 0 ? ids.join(',') : '0') as any,
        });
      }

      return filters;
    });

    // Initial sort - most recent first
    const initialSort: SpmActiveSort = {
      field: 'date_creation',
      order: 'desc',
    };

    // Columns configuration
    const columns = computed<SpmColumnConfig[]>(() => [
      {
        id: 'name',
        header: t('campaignsBulkList.columns.name'),
        maxWidth: '35%',
        fields: [
          {
            name: 'name',
            label: t('campaignsBulkList.columns.name'),
            sortable: true,
            filterable: true,
            filterConfig: { type: 'textInput' },
          },
          'id',
          {
            name: 'date_creation',
            label: t('campaignsBulkList.columns.createdAt'),
            sortable: true,
            filterable: true,
            filterConfig: { type: 'dateRange' },
          },
          {
            name: 'date_send',
            label: t('campaignsBulkList.columns.sentAt'),
            sortable: true,
            filterable: true,
            filterConfig: { type: 'dateRange' },
          },
          'primary_channel',
          'channels',
        ],
      },
      {
        id: 'status',
        header: t('campaignsBulkList.columns.status'),
        fields: [
          {
            name: 'status',
            label: t('campaignsBulkList.columns.status'),
            sortable: true,
            filterable: true,
            filterConfig: {
              type: 'singleSelect',
              options: statusOptions.value,
            },
          },
          'send_mode',
        ],
      },
      {
        id: 'performance',
        lazy: true,
        header: t('campaignsBulkList.columns.performance'),
        fields: [
          {
            name: 'open_rate',
            label: t('campaignsBulkList.metrics.openRate'),
            sortable: true,
          },
          {
            name: 'open_rate_total',
            label: t('campaigns.common.metrics.openRateTotal'),
            sortable: true,
          },
          {
            name: 'click_rate',
            label: t('campaignsBulkList.metrics.clickRate'),
            sortable: true,
          },
          'deliveries',
          'opens',
          'clicks',
        ],
      },
      {
        id: 'conversions',
        lazy: true,
        header: t('campaignsBulkList.columns.conversions'),
        fields: [
          {
            name: 'sales_amount',
            label: t('campaignsBulkList.metrics.salesAmount'),
            sortable: true,
          },
          {
            name: 'conversion_rate',
            label: t('campaignsBulkList.metrics.conversionRate'),
            sortable: true,
          },
          'sales',
          'bounces',
          'spam',
          'unsubscribes',
        ],
      },
    ]);

    /**
     * Reload table data after an action
     */
    const reloadTableData = () => {
      if (tableRef.value?.reloadData) {
        tableRef.value.reloadData();
      }
    };

    // Use the composable for actions
    const { rowActions, groupedActions } = useBulkCampaignsActions({
      duplicateRef,
      deleteRef,
      archiveRef,
      suspendRef,
      showNeedOfferModal,
      confirm,
      onRefresh: reloadTableData,
      onExport: (selection: SelectionInfo) => {
        console.log('Export selection:', selection);
        showToastSuccess(t('campaignsBulkList.export.success'));
      },
      onPublishToGallery: async (selection: SelectionInfo) => {
        if (selection.selectionCount === 0) return;

        try {
          setLoading(true);
          // Build query params based on selection mode
          const params: Record<string, any> = {
            fields: ['id', 'name'],
            limit: selection.selectionCount,
          };

          if (selection.selectAllMode) {
            // Select all mode: fetch all, exclude specific IDs if any
            if (selection.excludedRows.length > 0) {
              params.filters = [{ field: 'id', operator: 'not_in', values: selection.excludedRows.join(',') }];
            }
          } else {
            // Manual selection: fetch specific IDs
            params.filters = [{ field: 'id', operator: 'in', values: selection.selectedRows.join(',') }];
          }

          const response = await nestCall(
            'get',
            NestApiType.CLIENT_API,
            '/campaigns/bulk',
            {},
            '',
            undefined,
            true,
            params,
          );
          const campaignList = (response?.data || []).map((c: any) => ({ id: Number(c.id), name: c.name }));
          if (campaignList.length > 0 && publishGalleryRef.value?.open) {
            publishGalleryRef.value.open(campaignList);
          }
        } catch (error) {
          console.error('Publish to gallery error:', error);
          await showToastError(t('campaignsBulkList.groupedActions.publishToGalleryError'));
        } finally {
          setLoading(false);
        }
      },
    });

    /**
     * Reset table selection after a grouped action is confirmed
     */
    const resetTableSelection = () => {
      if (tableRef.value?.unselectAll) {
        tableRef.value.unselectAll();
      }
    };

    // Handler for action confirmations
    const onActionConfirm = () => {
      resetTableSelection();
      reloadTableData();
    };

    // Handler for publish to gallery dialog confirmation
    const onPublishToGalleryConfirm = async (payload: { campaignIds: number[]; campaignIdsWithoutTemplates: number[] }) => {
      try {
        setLoading(true);
        const response = await nestCall(
          'post',
          NestApiType.DEFAULT,
          '/data/publish-to-gallery',
          {},
          '',
          {
            shopId: UserState.activeShop?.id,
            campaignIds: payload.campaignIds,
            campaignIdsWithoutTemplates: payload.campaignIdsWithoutTemplates,
            type: 'campaign_bulk',
          },
          true,
        );
        if (response?.success) {
          await showToastSuccess(t('campaignsBulkList.groupedActions.publishToGallerySuccess'));
        } else {
          await showToastError(response?.message || t('campaignsBulkList.groupedActions.publishToGalleryError'));
        }
      } catch (error) {
        console.error('Publish to gallery error:', error);
        await showToastError(t('campaignsBulkList.groupedActions.publishToGalleryError'));
      } finally {
        if (publishGalleryRef.value?.done) {
          publishGalleryRef.value.done();
        }
        resetTableSelection();
        setLoading(false);
      }
    };

    // Navigate to MyOffer page
    const goToMyOffer = async () => {
      await router.push({ name: 'shop.MyOffer' });
    };

    const setLoading = async (loading: boolean) => {
      if (tableRef.value) {
        await tableRef.value.setLoading(loading);
      }
    };

    /**
     * Handle export action from SpmTableAPI
     */
    const handleExport = async (payload: ExportPayload) => {
      try {
        setLoading(true);
        // Build export request payload - already simplified from SpmTableAPI
        const exportRequest: Record<string, unknown> = {
          entity_type: payload.exportConfig.entityType,
          selection_type: payload.selectAllMode ? 'all' : 'manual',
          selected_ids: payload.selectedIds,
          total_count: payload.totalCount,
          ...payload.exportConfig.additionalParams,
        };

        // Add filters only if present (selectAllMode from selection, not "Export all" button)
        if (payload.filters) {
          exportRequest.filters = payload.filters;
        }

        console.log('Export request:', exportRequest);

        const response = await nestCall(
          'post',
          NestApiType.CLIENT_API,
          '/export/init',
          {},
          '',
          exportRequest,
          true,
        );

        if (response && !response.error) {
          await showToastSuccess(t('campaignsBulkList.export.success'));
        } else {
          await showToastSuccess(t('campaignsBulkList.export.error'));
        }
      } catch (error) {
        console.error('Export error:', error);
        await showToastError(t('campaignsBulkList.export.error'));
      } finally {
        resetTableSelection();
        reloadTableData();
        setLoading(false);
      }
    };

    // Formatting functions - using shared formatters
    const formatPercentage = (value: number | null | undefined): string => formatPercentageUtil(value, false);

    const formatAmount = (value: number | null | undefined): string => formatCurrency(value, 'EUR');

    // Tooltip for the date_modification badge: same DB field, different
    // semantic depending on the campaign status (suspended → stop date,
    // anything else → last edit).
    const getModificationTooltip = (status: string | null | undefined): string => {
      const isSuspended =
        status === 'suspended' || status === 'suspended_by_user';
      return isSuspended
        ? t('campaignsBulkList.dateTooltips.suspension')
        : t('campaignsBulkList.dateTooltips.modification');
    };

    const formatChannel = (value: string | null): string => {
      if (!value) return '-';
      const key = value.toLowerCase();
      const translationKey = `campaignsBulkList.channels.${key}`;
      const translated = t(translationKey);
      return translated === translationKey ? key : translated;
    };

    const extractChannelTags = (row: BulkCampaignData): string[] => {
      const uniqueChannels = new Set<string>();

      if (Array.isArray(row?.channels)) {
        row.channels.forEach((channel) => {
          if (channel?.channel) {
            uniqueChannels.add(channel.channel);
          }
        });
      }

      if (row?.primary_channel) {
        uniqueChannels.add(row.primary_channel);
      }

      return Array.from(uniqueChannels);
    };

    // Formatting functions for expansion - using shared formatters
    const formatInteger = (value: number | null | undefined): string => formatIntegerUtil(value);

    // Style functions - convert CSS vars format to SpmBadge format
    const getStatusStyle = (status: string | null | undefined) => {
      const vars = getStatusStyleVars(status);
      return {
        bg: vars['--status-bg'] || 'var(--surface-200)',
        color: vars['--status-color'] || 'var(--text-color)',
        borderColor: vars['--status-border'],
      };
    };
    const sendModeIcon = (mode: string): string => {
      if (mode === 'perfect_timing') return 'fa-light fa-clock';
      if (mode === 'slow_down') return 'fa-light fa-gauge-low';
      return '';
    };

    const sendModeLabel = (row: BulkCampaignData): string => {
      if (!row.send_mode || row.send_mode === 'standard') return '';
      const baseLabel = t(`campaignsBulkList.sendMode.${row.send_mode}.label`);
      if (row.send_mode === 'slow_down' && row.interval_between_sends) {
        return t('campaignsBulkList.sendMode.slow_down.labelWithInterval', {
          label: baseLabel,
          minutes: row.interval_between_sends,
        });
      }
      return baseLabel;
    };

    const sendModeTooltip = (row: BulkCampaignData): string => {
      if (!row.send_mode || row.send_mode === 'standard') return '';
      const baseTooltip = t(`campaignsBulkList.sendMode.${row.send_mode}.tooltip`);
      if (row.send_mode === 'slow_down' && row.interval_between_sends) {
        return t('campaignsBulkList.sendMode.slow_down.tooltipWithInterval', {
          tooltip: baseTooltip,
          minutes: row.interval_between_sends,
        });
      }
      return baseTooltip;
    };

    const getChannelTagStyle = (channel: string | null | undefined) => {
      const vars = getChannelTagStyleVars(channel);
      return {
        bg: vars['--channel-bg'] || 'var(--surface-200)',
        color: vars['--channel-color'] || 'var(--text-color)',
        borderColor: vars['--channel-border'],
      };
    };

    return {
      t,
      tableRef,
      apiCall,
      loadCampaignsStats,
      columns,
      persistentFilters,
      initialSort,
      rowActions,
      groupedActions,
      // Action component refs
      duplicateRef,
      deleteRef,
      archiveRef,
      suspendRef,
      publishGalleryRef,
      onActionConfirm,
      onPublishToGalleryConfirm,
      // Modal states
      showNeedOfferModal,
      showImportModal,
      showCreateModal,
      goToMyOffer,
      // Formatting functions
      formatPercentage,
      formatAmount,
      formatInteger,
      formatChannel,
      extractChannelTags,
      getModificationTooltip,
      // Style functions
      getStatusStyle,
      getStatusIcon,
      getChannelTagStyle,
      getChannelIcon,
      handleExport,
      clientTimezone,
      sendModeIcon,
      sendModeLabel,
      sendModeTooltip,
    };
  },
});
